import styled from 'styled-components';
import {
    GetManualSupplyOrdersQuery,
    GetManualSupplyOrderSupplierProductsStockVariationQuery,
} from 'data/__generated__';
import { colors } from 'constants/colors';
import { SectionContainer } from 'components/DetailsView/Section';
import { TotemCheckbox } from 'components/TotemCheckbox';
import { DetailFormDatePicker } from 'components/DetailsView/DetailFormDatePicker';
import { useFormContext } from 'react-hook-form';
import { dateFromString, formatDateAsAnniversary, transformFrenchToBirthDayDate } from 'helpers/dateTimes';
import { useCallback } from 'react';
import { renderAsPrice } from 'helpers/price';
import { getMinRequirementAsString } from 'pages/SupplyOrders/SupplyOrderDetails/SupplierOrderingTermsSummary';
import { DetailLink } from 'components/DetailsView/DetailLink';

// Fonction pour déterminer la couleur du label de date de commande
const getOrderDateLabelColor = (orderDate: string | null | undefined): 'red' | 'orange' | 'normal' => {
    if (!orderDate) return 'normal';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const orderDateObj = dateFromString(orderDate, false);
    if (!orderDateObj) return 'normal';

    const orderDateOnly = new Date(orderDateObj);
    orderDateOnly.setHours(0, 0, 0, 0);

    const timeDiff = orderDateOnly.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysDiff < 0) {
        return 'red'; // En retard
    } else if (daysDiff === 0) {
        return 'orange'; // À commander aujourd'hui
    }

    return 'normal';
};

// Fonction pour calculer les taux de stock insuffisant et critique par commande
const calculateStockRatesForSupplyOrder = ({
    supplyOrder,
    supplyOrderStockVariations,
}: {
    supplyOrder: GetManualSupplyOrdersQuery['manualSupplyOrders'][number];
    supplyOrderStockVariations: GetManualSupplyOrderSupplierProductsStockVariationQuery['manualSupplyOrderSupplierProductsStockVariation']['supplyOrderStockVariations'];
}): {
    insufficientStockRate: number;
    criticalStockRate: number;
    totalProducts: number;
    insufficientStockProducts: number;
    criticalStockProducts: number;
} => {
    let totalProducts = 0;
    let insufficientStockProducts = 0;
    let criticalStockProducts = 0;

    supplyOrder.products.forEach((product) => {
        totalProducts += 1;

        const stockVariationElem = supplyOrderStockVariations?.find(
            (stockVariation) => stockVariation.productId === product._id,
        );

        if (!stockVariationElem) {
            return; // RAS - pas de problème de stock
        }

        const { stockUpdateDayIndexIfOrderedOnNextOrderingDay, stockVariation, computedStockThreshold } =
            stockVariationElem;

        const stockBeforeNextPossibleOrder = stockVariation[stockUpdateDayIndexIfOrderedOnNextOrderingDay];

        if (stockBeforeNextPossibleOrder < 0) {
            // Stock critique
            criticalStockProducts += 1;
        } else if (stockBeforeNextPossibleOrder < computedStockThreshold) {
            // Stock insuffisant
            insufficientStockProducts += 1;
        }
    });

    const insufficientStockRate = totalProducts > 0 ? (insufficientStockProducts / totalProducts) * 100 : 0;
    const criticalStockRate = totalProducts > 0 ? (criticalStockProducts / totalProducts) * 100 : 0;

    return {
        insufficientStockRate,
        criticalStockRate,
        totalProducts,
        insufficientStockProducts,
        criticalStockProducts,
    };
};

type ForecastStatus = 'orange' | 'dark-orange' | 'red' | 'green' | 'none';

interface ForecastInfo {
    status: ForecastStatus;
    message: string;
    stockRates?: {
        insufficientStockRate: number;
        criticalStockRate: number;
        totalProducts: number;
        insufficientStockProducts: number;
        criticalStockProducts: number;
    };
}

export const SupplierStatusSection = ({
    manualSupplyOrders,
    stockVariationData,
    selectedSupplyOrderIds,
    setSelectedSupplyOrderIds,
}: {
    manualSupplyOrders: GetManualSupplyOrdersQuery['manualSupplyOrders'];
    stockVariationData?: GetManualSupplyOrderSupplierProductsStockVariationQuery['manualSupplyOrderSupplierProductsStockVariation'];
    selectedSupplyOrderIds: string[];
    setSelectedSupplyOrderIds: (ids: string[]) => void;
}) => {
    const { control } = useFormContext();

    const getForecastStatus = useCallback(
        (supplyOrder: GetManualSupplyOrdersQuery['manualSupplyOrders'][number]): ForecastInfo => {
            const supplyOrderStockVariations = stockVariationData?.supplyOrderStockVariations.filter(
                (stockVariation) => stockVariation.supplyOrderId === supplyOrder._id,
            );
            if (!supplyOrderStockVariations)
                return {
                    status: 'green',
                    message: 'RAS',
                };
            const stockRates = calculateStockRatesForSupplyOrder({
                supplyOrder,
                supplyOrderStockVariations,
            });

            if (stockRates.criticalStockRate > 0)
                return {
                    status: 'red',
                    message: 'Stock critique',
                    stockRates,
                };
            if (stockRates.insufficientStockRate > 0)
                return {
                    status: 'orange',
                    message: 'Stock insuffisant',
                    stockRates,
                };
            return {
                status: 'green',
                message: 'RAS',
            };
        },
        [stockVariationData],
    );

    const handleSupplyOrderSelection = useCallback(
        (supplyOrderId: string) => {
            const isSelected = selectedSupplyOrderIds.includes(supplyOrderId);
            if (isSelected) {
                setSelectedSupplyOrderIds(selectedSupplyOrderIds.filter((id) => id !== supplyOrderId));
            } else {
                setSelectedSupplyOrderIds([...selectedSupplyOrderIds, supplyOrderId]);
            }
        },
        [selectedSupplyOrderIds, setSelectedSupplyOrderIds],
    );

    return (
        <SectionContainer title={`État des achats manuels (${manualSupplyOrders.length})`} isInitiallyOpen>
            <SuppliersGrid>
                {manualSupplyOrders.map((supplyOrder, index) => {
                    const forecast = getForecastStatus(supplyOrder);
                    const stockRates = forecast.stockRates;
                    const { orderingTermsValidity } = supplyOrder;
                    const franco = supplyOrder.supplier.orderingTerms.find(
                        (orderingTerm) => orderingTerm.deliveryPrice === 0,
                    );

                    return (
                        <SupplierCard key={supplyOrder._id}>
                            <SupplierCardHeader>
                                <DetailLink
                                    path="supply-order"
                                    value={supplyOrder._id}
                                    name={supplyOrder.supplier.name}
                                    color="black"
                                    fontWeight="bold"
                                />
                                <TotemCheckbox
                                    checked={selectedSupplyOrderIds.includes(supplyOrder._id)}
                                    onChange={() => handleSupplyOrderSelection(supplyOrder._id)}
                                />
                            </SupplierCardHeader>
                            <SupplierDetails>
                                <DetailItem>
                                    <Label>Prix HT</Label>
                                    <DetailValue>
                                        {supplyOrder.priceHT !== null
                                            ? renderAsPrice(supplyOrder.priceHT)
                                            : 'Non calculé'}
                                    </DetailValue>
                                </DetailItem>
                                <DetailItem>
                                    <Label>Franco</Label>
                                    <DetailValue $isValid={orderingTermsValidity?.isValid}>
                                        {franco
                                            ? `Min ${
                                                  getMinRequirementAsString(franco.minRequirements) !== ''
                                                      ? getMinRequirementAsString(franco.minRequirements)
                                                      : 'Pas de condition'
                                              }`
                                            : 'N/A'}
                                    </DetailValue>
                                </DetailItem>
                                <DetailItem>
                                    <Label>Forecast</Label>
                                    <ForecastBadge status={forecast.status}>{forecast.message}</ForecastBadge>
                                    {stockRates && stockRates.insufficientStockRate > 0 && (
                                        <StockRateValue $status="orange">
                                            {stockRates.insufficientStockRate.toFixed(1)}% (
                                            {stockRates.insufficientStockProducts}/{stockRates.totalProducts})
                                        </StockRateValue>
                                    )}
                                    {stockRates && stockRates.criticalStockRate > 0 && (
                                        <StockRateValue $status="red">
                                            {stockRates.criticalStockRate.toFixed(1)}% (
                                            {stockRates.criticalStockProducts}/{stockRates.totalProducts})
                                        </StockRateValue>
                                    )}
                                </DetailItem>
                                <DateFieldContainer>
                                    <Label $color={getOrderDateLabelColor(supplyOrder.orderDate)}>
                                        Date de commande
                                    </Label>
                                    <DetailFormDatePicker
                                        name={`supplyOrders.${index}.orderDate`}
                                        defaultValue={supplyOrder.orderDate}
                                        control={control}
                                        transformValueToDate={(value) => (value ? dateFromString(value, false) : null)}
                                        transformDateToValue={(date) =>
                                            date
                                                ? formatDateAsAnniversary({ dateTime: date, useNewFormat: false })
                                                : null
                                        }
                                        isClearable
                                        compact
                                    />
                                </DateFieldContainer>
                                <DateFieldContainer>
                                    <Label>Date de livraison prévue</Label>
                                    <DetailFormDatePicker
                                        name={`supplyOrders.${index}.dateDeliveryScheduled`}
                                        defaultValue={supplyOrder.dateDeliveryScheduled}
                                        control={control}
                                        transformValueToDate={(value) => (value ? dateFromString(value, false) : null)}
                                        transformDateToValue={(date) =>
                                            date
                                                ? formatDateAsAnniversary({ dateTime: date, useNewFormat: false })
                                                : null
                                        }
                                        isClearable
                                        compact
                                    />
                                </DateFieldContainer>
                                <DateFieldContainer>
                                    <Label>Date de la prochaine commande</Label>
                                    <DetailFormDatePicker
                                        name={`supplyOrders.${index}.nextOrderDate`}
                                        defaultValue={supplyOrder.nextOrderDate}
                                        control={control}
                                        transformValueToDate={(value) => (value ? dateFromString(value, true) : null)}
                                        transformDateToValue={(date) =>
                                            date
                                                ? formatDateAsAnniversary({ dateTime: date, useNewFormat: true })
                                                : null
                                        }
                                        isClearable
                                        compact
                                    />
                                </DateFieldContainer>
                                {!supplyOrder.scheduledDeliveryDateValidity ? (
                                    <Warning>
                                        La date de livraison prévue est incompatible avec la date de commande
                                    </Warning>
                                ) : null}
                                {supplyOrder.dateDeliveryScheduled &&
                                    supplyOrder.nextOrderDate &&
                                    supplyOrder.nextOrderDate <
                                        transformFrenchToBirthDayDate(supplyOrder.dateDeliveryScheduled) && (
                                        <Warning>
                                            La date de la prochaine commande est antérieure à la date de livraison.
                                            Veuillez mettre à jour la date de prochaine commande.
                                        </Warning>
                                    )}
                            </SupplierDetails>
                        </SupplierCard>
                    );
                })}
            </SuppliersGrid>
        </SectionContainer>
    );
};

// Styles
const Warning = styled.div<{ $color?: string }>`
    color: ${({ theme, $color }) => $color ?? theme.errorColor};
    font-weight: 500;
    font-size: 14px;
`;

const StockRateValue = styled.span.withConfig({
    shouldForwardProp: (prop) => !['$status'].includes(prop),
})<{ $status: 'orange' | 'red' | 'neutral' }>`
    font-size: 12px;
    font-weight: 600;
    color: ${({ $status }) => {
        switch ($status) {
            case 'orange':
                return colors.orange;
            case 'red':
                return colors.red;
            case 'neutral':
                return colors.darkGrey;
            default:
                return colors.darkGrey;
        }
    }};
`;

const SuppliersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
`;

const SupplierCard = styled.div`
    padding: 16px;
    border: 1px solid ${colors.lightGrey};
    border-radius: 6px;
    background: ${colors.whiteGrey};
`;

const SupplierCardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
`;

const SupplierName = styled.h4`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: ${colors.darkGrey};
`;

const SupplierDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const DateFieldContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const DetailItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const Label = styled.span.withConfig({
    shouldForwardProp: (prop) => !['$color'].includes(prop),
})<{ $color?: 'red' | 'orange' | 'normal' }>`
    font-size: 12px;
    color: ${({ $color }) => {
        switch ($color) {
            case 'red':
                return colors.red;
            case 'orange':
                return colors.orange;
            default:
                return colors.pantinGrey;
        }
    }};
    font-weight: 500;
`;

const ForecastBadge = styled.span.withConfig({
    shouldForwardProp: (prop) => !['status'].includes(prop),
})<{ status: ForecastStatus }>`
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    background-color: ${({ status }) => {
        switch (status) {
            case 'orange':
                return '#fef3c7';
            case 'dark-orange':
                return '#fed7aa';
            case 'red':
                return '#fef2f2';
            case 'green':
                return '#f0fdf4';
            default:
                return colors.whiteGrey;
        }
    }};
    color: ${({ status }) => {
        switch (status) {
            case 'orange':
                return colors.orange;
            case 'dark-orange':
                return '#ea580c';
            case 'red':
                return colors.red;
            case 'green':
                return colors.green;
            default:
                return colors.pantinGrey;
        }
    }};
`;

const DetailValue = styled.span<{ $isValid?: boolean }>`
    font-size: 14px;
    font-weight: 800;
    margin-left: 5px;
    text-align: right;
    color: ${({ $isValid, theme }) =>
        $isValid !== undefined ? ($isValid ? theme.successColor : theme.warningColor) : theme.textColor};
`;
